import { toast } from 'sonner';
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, X, RefreshCw, FlaskConical, CheckCircle, Clock, Archive, Lock, Unlock, ShieldAlert, Send, Sparkles, MessageSquare, TrendingUp, IndianRupee, AlertTriangle } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { useAdminDelete } from '../hooks/useAdminDelete';
import { ConfirmDeleteModal } from '../components/ConfirmDeleteModal';
import ProductCodeBadge from '../components/ProductCodeBadge';
import MobileModal from '../components/MobileModal';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const OUTCOMES = ['ongoing', 'success', 'partial', 'failed'];
const STATUSES = ['Active Trial', 'Under Review', 'Approved', 'Archived'];
const APPLICATIONS = ['snack', 'seasoning', 'beverage', 'sauce', 'marinade', 'other'];

const OUTCOME_STYLE = {
  ongoing:  'bg-blue-900/40 text-blue-300 border-blue-700',
  success:  'bg-green-900/40 text-green-300 border-green-700',
  partial:  'bg-amber-900/40 text-amber-300 border-amber-700',
  failed:   'bg-red-900/40 text-red-300 border-red-700',
};
const STATUS_STYLE = {
  'Active Trial':  'bg-blue-900/40 text-blue-300 border-blue-700',
  'Under Review':  'bg-amber-900/40 text-amber-300 border-amber-700',
  'Approved':      'bg-green-900/40 text-green-300 border-green-700',
  'Archived':      'bg-zinc-800 text-zinc-500 border-zinc-700',
};

const emptyForm = {
  date: new Date().toISOString().slice(0, 10),
  product_name: '', batch_code: '', outcome: 'ongoing',
  application: 'other', target_client: '', notes: '',
  status: 'Active Trial', pricing: '', costing: '',
  ingredients: [],
  overhead_costs: [],
  batch_size_kg: 1,
};

// Same overhead categories as backend cost_variance.py classifier so this ties
// into the existing cost-variance reporting language.
const OVERHEAD_CATEGORIES = [
  'Electricity', 'Labour', 'Rent', 'Packaging', 'Freight-Transport',
  'Machinery-Maintenance', 'Utilities', 'Marketing', 'Professional Fees',
  'Office', 'Misc',
];

export default function RnD() {
  const [unlocked, setUnlocked] = useState(false);
  const [pw, setPw] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [tab, setTab] = useState('trials');

  const [trials, setTrials] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [filterStatus, setFilterStatus] = useState('All');

  const unlock = async (e) => {
    e?.preventDefault();
    if (!pw) return;
    setVerifying(true);
    try {
      await axios.post(`${API}/rnd/verify-password`, { password: pw }, { withCredentials: true });
      setUnlocked(true);
      toast.success('R&D module unlocked');
    } catch (err) { toast.error(err.response?.data?.detail || 'Incorrect password'); }
    finally { setVerifying(false); }
  };

  const fetchTrials = async () => {
    setLoading(true);
    try {
      const [tRes, mRes] = await Promise.all([
        axios.get(`${API}/rnd/trials`, { withCredentials: true }),
        axios.get(`${API}/inventory/raw-materials`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setTrials(tRes.data);
      setMaterials(mRes.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const { deleteState, requestDelete, confirmDelete, cancelDelete, isAdmin } = useAdminDelete(fetchTrials);

  useEffect(() => { if (unlocked) fetchTrials(); }, [unlocked]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      date: t.date, product_name: t.product_name, batch_code: t.batch_code || '',
      outcome: t.outcome, application: t.application || 'other', target_client: t.target_client || '',
      notes: t.notes || '', status: t.status, pricing: t.pricing || '', costing: t.costing || '',
      ingredients: Array.isArray(t.ingredients) ? t.ingredients : [],
      overhead_costs: Array.isArray(t.overhead_costs) ? t.overhead_costs : [],
      batch_size_kg: t.batch_size_kg || 1,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editing) {
        const res = await axios.put(`${API}/rnd/trials/${editing.id}`, form, { withCredentials: true });
        setTrials(prev => prev.map(t => t.id === editing.id ? res.data : t));
      } else {
        const res = await axios.post(`${API}/rnd/trials`, form, { withCredentials: true });
        setTrials(prev => [res.data, ...prev]);
      }
      setShowModal(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSubmitting(false); }
  };

  const deleteTrial = (id, name) => {
    requestDelete(`/rnd/trials/${id}`, name || 'Trial', `Delete R&D trial "${name || 'this trial'}" permanently.`);
  };

  // Push an R&D trial into the main Recipes collection as a starter recipe
  const pushToRecipes = async (t) => {
    if (!t.product_name) { toast.error('Trial needs a product name'); return; }
    const ings = Array.isArray(t.ingredients) ? t.ingredients.filter(i => (i.name || '').trim() && (parseFloat(i.qty) || 0) > 0) : [];
    const extraMsg = ings.length ? ` with ${ings.length} ingredient(s)` : ' (no ingredients — you can fill them in Recipes)';
    if (!window.confirm(`Push "${t.product_name}" to main Recipes${extraMsg}?`)) return;

    // Recipes are password-gated — ensure we have a valid token.
    let recipeToken = sessionStorage.getItem('ti_recipe_token') || '';
    if (!recipeToken) {
      const pw = window.prompt('Enter the Recipe password to push this trial into Recipes:');
      if (!pw) return;
      try {
        const res = await axios.post(`${API}/production/recipes/verify-password`,
          { password: pw }, { withCredentials: true });
        recipeToken = res.data?.token || pw;
        sessionStorage.setItem('ti_recipe_token', recipeToken);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Incorrect recipe password');
        return;
      }
    }

    try {
      const pid = (t.product_name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'trial-' + t.id.slice(0, 6);
      const recipeMaterials = ings.map(i => ({
        material_name: i.name,
        qty: parseFloat(i.qty) || 0,
        unit: i.unit || 'g',
        rate: parseFloat(i.rate) || 0,
      }));
      // batch_size_kg = sum of ingredient quantities (matching the recipe layer's rule).
      // R&D trials usually capture qtys in grams, so convert to kg.
      const computedKg = ings.reduce((s, i) => {
        const q = parseFloat(i.qty) || 0;
        const u = (i.unit || 'g').toLowerCase();
        return s + (u === 'kg' ? q : u === 'g' ? q / 1000 : q);
      }, 0);
      await axios.post(`${API}/production/recipes`, {
        product_id: pid,
        product_name: t.product_name,
        product_code: t.product_code || '',
        batch_size_kg: Math.round(computedKg * 100) / 100,
        notes: `Created from R&D trial ${t.batch_code || t.id} (${t.date})`,
        materials: recipeMaterials,
      }, { withCredentials: true, headers: { 'X-Recipe-Token': recipeToken } });
      toast.success(`Pushed to Recipes${extraMsg}`);
    } catch (e) {
      // If token was rejected, clear it so the next attempt re-prompts.
      if (e.response?.status === 401) sessionStorage.removeItem('ti_recipe_token');
      toast.error(e.response?.data?.detail || 'Failed to push to Recipes');
    }
  };

  const filtered = filterStatus === 'All' ? trials : trials.filter(t => t.status === filterStatus);

  const statCounts = {
    active: trials.filter(t => t.status === 'Active Trial').length,
    approved: trials.filter(t => t.status === 'Approved').length,
    success: trials.filter(t => t.outcome === 'success').length,
  };

  if (!unlocked) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <form onSubmit={unlock} className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 w-full max-w-md space-y-4" data-testid="rnd-gate">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
              <ShieldAlert size={18} className="text-amber-300" />
            </div>
            <div>
              <h2 className="font-heading font-semibold text-zinc-50 text-base">R&D — Locked</h2>
              <p className="text-zinc-500 text-xs">Enter the secondary password. Admins are notified on every unlock.</p>
            </div>
          </div>
          <div>
            <label className="text-zinc-400 text-xs mb-1 block">Secondary Password</label>
            <input type="password" value={pw} onChange={e => setPw(e.target.value)} autoFocus
              data-testid="rnd-password"
              placeholder="Secondary password"
              className="w-full bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
          </div>
          <button type="submit" disabled={verifying || !pw} data-testid="rnd-unlock-btn"
            className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:bg-zinc-700 disabled:text-zinc-400 text-black rounded-lg py-2.5 text-sm font-semibold transition-all">
            {verifying ? 'Verifying…' : <><Unlock size={14} /> Unlock</>}
          </button>
          <p className="text-[10px] text-zinc-600 text-center">All access attempts are logged and admins receive a notification.</p>
        </form>
      </div>
    );
  }

  if (loading) return <div className="text-zinc-400 text-sm animate-pulse">Loading R&D trials...</div>;

  return (
    <div className="space-y-5" data-testid="rnd-page">
      {/* Tab switcher */}
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setTab('trials')} data-testid="rnd-tab-trials"
          className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-xs font-semibold transition-all ${tab === 'trials' ? 'bg-amber-500/15 border-amber-500/40 text-amber-300' : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-zinc-200'}`}>
          <FlaskConical size={13} /> Trials
        </button>
        <button onClick={() => setTab('chat')} data-testid="rnd-tab-chat"
          className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-xs font-semibold transition-all ${tab === 'chat' ? 'bg-amber-500/15 border-amber-500/40 text-amber-300' : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-zinc-200'}`}>
          <Sparkles size={13} /> R&D AI Chat
        </button>
      </div>

      {tab === 'chat' ? (
        <RndChat />
      ) : (
        <>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-zinc-50">R&D Trials</h1>
          <p className="text-zinc-500 text-sm mt-0.5">{trials.length} trials · Recipe & costing experiments</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchTrials} className="p-2 rounded-xl border border-zinc-800 text-zinc-400 hover:bg-zinc-800 transition-colors"><RefreshCw size={14} /></button>
          <button data-testid="add-trial-btn" onClick={openCreate} className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs px-3 py-2 rounded-xl transition-colors">
            <Plus size={14} /> New Trial
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Active Trials', value: statCounts.active, icon: FlaskConical, color: 'text-blue-400' },
          { label: 'Approved', value: statCounts.approved, icon: CheckCircle, color: 'text-green-400' },
          { label: 'Successful Outcomes', value: statCounts.success, icon: Clock, color: 'text-amber-400' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-zinc-800 flex items-center justify-center flex-shrink-0">
              <Icon size={16} className={color} />
            </div>
            <div>
              <div className="text-zinc-50 text-xl font-heading font-bold">{value}</div>
              <div className="text-zinc-500 text-xs">{label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Status Filter */}
      <div className="flex gap-2 flex-wrap">
        {['All', ...STATUSES].map(s => (
          <button key={s} onClick={() => setFilterStatus(s)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${filterStatus === s ? 'bg-amber-500 text-black border-amber-500' : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-zinc-200'}`}
            data-testid={`filter-${s.replace(/\s+/g, '-').toLowerCase()}`}
          >{s}</button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-800">
              {['Date', 'Product', 'Batch Code', 'Application', 'Target Client', 'Outcome', 'Status', 'Cost/kg', ''].map(h => (
                <th key={h} className="text-left text-zinc-500 text-xs font-medium px-4 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(t => (
              <tr key={t.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors" data-testid={`trial-row-${t.id}`}>
                <td className="px-4 py-3 text-zinc-400 text-xs">{t.date}</td>
                <td className="px-4 py-3 text-zinc-200 text-xs font-medium">
                  <span className="inline-flex items-center gap-1.5 flex-wrap">
                    <ProductCodeBadge name={t.product_name} code={t.product_code} />
                    {t.product_name}
                  </span>
                </td>
                <td className="px-4 py-3 font-mono text-amber-400 text-xs">{t.batch_code || '—'}</td>
                <td className="px-4 py-3 text-zinc-400 text-xs capitalize">{t.application || '—'}</td>
                <td className="px-4 py-3 text-zinc-400 text-xs">{t.target_client || '—'}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full border capitalize ${OUTCOME_STYLE[t.outcome] || ''}`}>{t.outcome}</span>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLE[t.status] || ''}`}>{t.status}</span>
                </td>
                <td className="px-4 py-3">
                  <TrialCostBadge trial={t} />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <button data-testid={`push-recipe-${t.id}`} title="Push to Recipes"
                      onClick={() => pushToRecipes(t)}
                      className="p-1 rounded bg-amber-500/15 text-amber-300 hover:bg-amber-500/30">
                      <FlaskConical size={12} />
                    </button>
                    <button data-testid={`edit-trial-${t.id}`} onClick={() => openEdit(t)} className="text-zinc-500 hover:text-amber-400 transition-colors"><Edit2 size={13} /></button>
                    {isAdmin && <button data-testid={`delete-trial-${t.id}`} onClick={() => deleteTrial(t.id, t.product_name)} className="text-zinc-500 hover:text-red-400 transition-colors"><Trash2 size={13} /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="text-center text-zinc-500 text-sm py-10">No trials found</div>}
      </div>

      {/* Modal */}
      {showModal && (
        <MobileModal onClose={() => setShowModal(false)} maxWidth="lg" testId="rnd-trial-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800 sticky top-0 bg-zinc-900 z-10 rounded-t-2xl">
              <h3 className="font-heading font-semibold text-zinc-50">{editing ? 'Edit Trial' : 'New R&D Trial'}</h3>
              <button onClick={() => setShowModal(false)}><X size={18} className="text-zinc-500 hover:text-zinc-200" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4" data-testid="trial-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Date</label>
                  <input type="date" value={form.date} onChange={e => setForm(f => ({...f, date: e.target.value}))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Batch Code</label>
                  <input value={form.batch_code} onChange={e => setForm(f => ({...f, batch_code: e.target.value}))} placeholder="e.g. RND-001" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div className="col-span-2">
                  <label className="text-zinc-400 text-xs mb-1.5 block">Product Name *</label>
                  <input required data-testid="trial-product-input" value={form.product_name} onChange={e => setForm(f => ({...f, product_name: e.target.value}))} placeholder="e.g. Peri Peri Seasoning 151" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Application</label>
                  <select value={form.application} onChange={e => setForm(f => ({...f, application: e.target.value}))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500">
                    {APPLICATIONS.map(a => <option key={a} value={a} className="bg-zinc-900 capitalize">{a}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Outcome</label>
                  <select value={form.outcome} onChange={e => setForm(f => ({...f, outcome: e.target.value}))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500">
                    {OUTCOMES.map(o => <option key={o} value={o} className="bg-zinc-900 capitalize">{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Status</label>
                  <select value={form.status} onChange={e => setForm(f => ({...f, status: e.target.value}))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500">
                    {STATUSES.map(s => <option key={s} value={s} className="bg-zinc-900">{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Target Client</label>
                  <input value={form.target_client} onChange={e => setForm(f => ({...f, target_client: e.target.value}))} placeholder="e.g. Haldiram's" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Pricing (₹/kg)</label>
                  <input type="number" step="any" value={form.pricing} onChange={e => setForm(f => ({...f, pricing: e.target.value}))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Costing (₹/kg)</label>
                  <input type="number" step="any" value={form.costing} onChange={e => setForm(f => ({...f, costing: e.target.value}))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div className="col-span-2">
                  <label className="text-zinc-400 text-xs mb-1.5 block flex items-center justify-between">
                    <span>Ingredients <span className="text-zinc-600">({(form.ingredients || []).length} rows)</span></span>
                    <span className="flex items-center gap-2">
                      <span className="text-zinc-500 text-[10px]">Batch Size</span>
                      <input
                        type="number" step="any" min="0.1"
                        value={form.batch_size_kg}
                        onChange={e => setForm(f => ({ ...f, batch_size_kg: parseFloat(e.target.value) || 1 }))}
                        className="w-16 bg-zinc-900 border border-zinc-700 rounded px-1.5 py-0.5 text-zinc-50 text-[11px] text-right focus:outline-none focus:border-amber-500"
                        data-testid="trial-batch-size"
                      />
                      <span className="text-zinc-500 text-[10px]">kg</span>
                    </span>
                  </label>
                  <TrialIngredientEditor
                    rows={form.ingredients || []}
                    materials={materials}
                    onChange={(rows) => setForm(f => ({ ...f, ingredients: rows }))}
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-zinc-400 text-xs mb-1.5 block flex items-center justify-between">
                    <span>Overhead Costs <span className="text-zinc-600">({(form.overhead_costs || []).length} rows)</span></span>
                    <span className="text-zinc-500 text-[10px]">electricity · labour · rent · packaging · transport · misc</span>
                  </label>
                  <TrialOverheadEditor
                    rows={form.overhead_costs || []}
                    onChange={(rows) => setForm(f => ({ ...f, overhead_costs: rows }))}
                  />
                </div>
                <div className="col-span-2">
                  <TrialCostSummary
                    ingredients={form.ingredients || []}
                    overheads={form.overhead_costs || []}
                    batchSizeKg={form.batch_size_kg}
                    pricing={parseFloat(form.pricing) || 0}
                    onUsePricingSuggestion={(cost) => setForm(f => ({ ...f, costing: cost.toFixed(2) }))}
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-zinc-400 text-xs mb-1.5 block">Notes</label>
                  <textarea rows={3} value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} placeholder="Trial notes, observations..." className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500 resize-none" />
                </div>
              </div>
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-2.5 rounded-xl border border-zinc-700 text-zinc-400 hover:bg-zinc-800 text-sm transition-colors">Cancel</button>
                <button data-testid="submit-trial-btn" type="submit" disabled={submitting} className="flex-1 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-semibold text-sm disabled:opacity-50 transition-colors">
                  {submitting ? 'Saving...' : (editing ? 'Update Trial' : 'Create Trial')}
                </button>
              </div>
            </form>
        </MobileModal>
      )}
      <ConfirmDeleteModal open={deleteState.open} onClose={cancelDelete} onConfirm={confirmDelete} title={deleteState.title} message={deleteState.message} />
        </>
      )}
    </div>
  );
}

// ─── Inline ingredient editor for R&D trial modal (+ rows) ──────────────────
function TrialIngredientEditor({ rows, materials, onChange }) {
  const updateRow = (idx, patch) => {
    const next = rows.slice();
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const addRow = () => onChange([...rows, { name: '', qty: '', unit: 'g', rate: '' }]);
  const removeRow = (idx) => onChange(rows.filter((_, i) => i !== idx));

  const resolveMaterial = (name) => materials.find(m => (m.name || '').toLowerCase() === (name || '').toLowerCase());

  return (
    <div className="bg-zinc-950/60 border border-zinc-800 rounded-xl p-2 space-y-1" data-testid="trial-ingredient-editor">
      <datalist id="trial-materials-dl">
        {materials.map(m => <option key={m.id} value={m.name} />)}
      </datalist>
      {rows.length === 0 && (
        <div className="text-center text-zinc-500 text-[11px] py-3">No ingredients yet. Click + to add your first row.</div>
      )}
      {rows.map((r, idx) => {
        const ref = resolveMaterial(r.name);
        const lineCost = (parseFloat(r.qty) || 0) * (parseFloat(r.rate) || 0);
        // Rate-spike detection — compare this trial's rate vs current master rate.
        let spikePct = null;
        if (ref && ref.unit_price > 0 && parseFloat(r.rate) > 0) {
          spikePct = ((parseFloat(r.rate) - ref.unit_price) / ref.unit_price) * 100;
        }
        const spikeBad = spikePct !== null && spikePct >= 10;      // trial using 10%+ above market
        const spikeWarn = spikePct !== null && spikePct >= 5 && spikePct < 10;
        return (
          <div key={idx} className="grid grid-cols-12 gap-1.5 items-center" data-testid={`trial-ing-row-${idx}`}>
            <input
              value={r.name || ''}
              onChange={e => {
                const name = e.target.value;
                const matched = resolveMaterial(name);
                updateRow(idx, {
                  name,
                  unit: r.unit || matched?.unit || 'g',
                  rate: r.rate !== '' && r.rate != null ? r.rate : (matched?.unit_price ?? ''),
                });
              }}
              list="trial-materials-dl"
              placeholder="Ingredient name"
              className="col-span-4 bg-zinc-900 border border-zinc-800 rounded-lg px-2 py-1.5 text-zinc-50 text-xs focus:outline-none focus:border-amber-500"
              data-testid={`trial-ing-name-${idx}`}
            />
            <input
              type="number" step="any" min="0"
              value={r.qty ?? ''}
              onChange={e => updateRow(idx, { qty: e.target.value })}
              placeholder="Qty"
              className="col-span-2 bg-zinc-900 border border-zinc-800 rounded-lg px-2 py-1.5 text-zinc-50 text-xs focus:outline-none focus:border-amber-500"
              data-testid={`trial-ing-qty-${idx}`}
            />
            <select
              value={r.unit || 'g'}
              onChange={e => updateRow(idx, { unit: e.target.value })}
              className="col-span-1 bg-zinc-900 border border-zinc-800 rounded-lg px-1 py-1.5 text-zinc-50 text-xs focus:outline-none"
              data-testid={`trial-ing-unit-${idx}`}
            >
              <option value="g">g</option><option value="kg">kg</option>
              <option value="ml">ml</option><option value="L">L</option>
              <option value="unit">u</option>
            </select>
            <input
              type="number" step="any" min="0"
              value={r.rate ?? ''}
              onChange={e => updateRow(idx, { rate: e.target.value })}
              placeholder="₹/unit"
              className={`col-span-2 bg-zinc-900 border rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-amber-500 ${
                spikeBad ? 'border-red-700 text-red-300' : spikeWarn ? 'border-amber-700 text-amber-300' : 'border-zinc-800 text-zinc-50'
              }`}
              data-testid={`trial-ing-rate-${idx}`}
              title={ref?.unit_price ? `Master rate: ₹${ref.unit_price}/${ref.unit || 'unit'}` : ''}
            />
            <div className="col-span-2 flex items-center justify-end gap-1 text-[10px] font-mono">
              {spikePct !== null && Math.abs(spikePct) >= 5 && (
                <span
                  className={`inline-flex items-center gap-0.5 ${spikeBad ? 'text-red-400' : 'text-amber-400'}`}
                  title={`Rate is ${spikePct >= 0 ? 'above' : 'below'} master ₹${ref.unit_price}/${ref.unit || 'unit'} by ${spikePct.toFixed(1)}%`}
                  data-testid={`trial-ing-spike-${idx}`}
                >
                  <TrendingUp size={10} className={spikePct < 0 ? 'rotate-180' : ''} />
                  {spikePct >= 0 ? '+' : ''}{spikePct.toFixed(0)}%
                </span>
              )}
              <span className="text-zinc-400 truncate" title={`Line cost: ₹${lineCost.toFixed(2)}`}>
                {lineCost > 0 ? `₹${lineCost.toFixed(0)}` : '—'}
              </span>
            </div>
            <button
              type="button"
              onClick={() => removeRow(idx)}
              className="col-span-1 text-zinc-500 hover:text-red-400 flex items-center justify-center py-1"
              data-testid={`trial-ing-remove-${idx}`}
              title="Remove row"
            >
              <X size={13} />
            </button>
          </div>
        );
      })}
      <button
        type="button"
        onClick={addRow}
        className="w-full mt-1 flex items-center justify-center gap-1.5 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border border-amber-500/30 rounded-lg py-1.5 text-xs font-medium transition-colors"
        data-testid="trial-ing-add-row"
      >
        <Plus size={12} /> Add ingredient row
      </button>
    </div>
  );
}

// ─── Overhead cost editor for R&D trial modal ──────────────────────────────
function TrialOverheadEditor({ rows, onChange }) {
  const update = (idx, patch) => {
    const next = rows.slice();
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const addRow = () => onChange([...rows, { category: 'Electricity', description: '', amount: '' }]);
  const removeRow = (idx) => onChange(rows.filter((_, i) => i !== idx));

  return (
    <div className="bg-zinc-950/60 border border-zinc-800 rounded-xl p-2 space-y-1" data-testid="trial-overhead-editor">
      {rows.length === 0 && (
        <div className="text-center text-zinc-500 text-[11px] py-3">No overheads yet. Click + to allocate electricity, labour, rent, packaging, etc.</div>
      )}
      {rows.map((r, idx) => {
        const amt = parseFloat(r.amount) || 0;
        return (
          <div key={idx} className="grid grid-cols-12 gap-1.5 items-center" data-testid={`trial-oh-row-${idx}`}>
            <select
              value={r.category || 'Misc'}
              onChange={e => update(idx, { category: e.target.value })}
              className="col-span-3 bg-zinc-900 border border-zinc-800 rounded-lg px-2 py-1.5 text-zinc-50 text-xs focus:outline-none focus:border-amber-500"
              data-testid={`trial-oh-cat-${idx}`}
            >
              {OVERHEAD_CATEGORIES.map(c => <option key={c} value={c} className="bg-zinc-900">{c}</option>)}
            </select>
            <input
              value={r.description || ''}
              onChange={e => update(idx, { description: e.target.value })}
              placeholder="Description (optional)"
              className="col-span-6 bg-zinc-900 border border-zinc-800 rounded-lg px-2 py-1.5 text-zinc-50 text-xs focus:outline-none focus:border-amber-500"
              data-testid={`trial-oh-desc-${idx}`}
            />
            <input
              type="number" step="any" min="0"
              value={r.amount ?? ''}
              onChange={e => update(idx, { amount: e.target.value })}
              placeholder="₹ amount"
              className="col-span-2 bg-zinc-900 border border-zinc-800 rounded-lg px-2 py-1.5 text-zinc-50 text-xs focus:outline-none focus:border-amber-500"
              data-testid={`trial-oh-amt-${idx}`}
            />
            <button
              type="button"
              onClick={() => removeRow(idx)}
              className="col-span-1 text-zinc-500 hover:text-red-400 flex items-center justify-center py-1"
              data-testid={`trial-oh-remove-${idx}`}
              title="Remove row"
            >
              <X size={13} />
            </button>
            {amt > 0 && <div className="col-span-12 -mt-1 text-right text-[10px] text-zinc-500 font-mono">₹{amt.toFixed(2)}</div>}
          </div>
        );
      })}
      <button
        type="button"
        onClick={addRow}
        className="w-full mt-1 flex items-center justify-center gap-1.5 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border border-amber-500/30 rounded-lg py-1.5 text-xs font-medium transition-colors"
        data-testid="trial-oh-add-row"
      >
        <Plus size={12} /> Add overhead row
      </button>
    </div>
  );
}

// ─── Live cost summary card ────────────────────────────────────────────────
function TrialCostSummary({ ingredients, overheads, batchSizeKg, pricing, onUsePricingSuggestion }) {
  const ingCost = (ingredients || []).reduce((s, i) => s + ((parseFloat(i.qty) || 0) * (parseFloat(i.rate) || 0)), 0);
  const ohCost = (overheads || []).reduce((s, o) => s + (parseFloat(o.amount) || 0), 0);
  const totalCost = ingCost + ohCost;
  const bs = parseFloat(batchSizeKg) || 1;
  const costPerKg = totalCost / bs;
  const marginPct = pricing > 0 ? ((pricing - costPerKg) / pricing) * 100 : null;
  const marginBad = marginPct !== null && marginPct < 0;
  const marginLow = marginPct !== null && marginPct >= 0 && marginPct < 15;

  return (
    <div className="bg-gradient-to-br from-amber-500/5 to-zinc-900/40 border border-zinc-800 rounded-xl p-3" data-testid="trial-cost-summary">
      <div className="flex items-center gap-2 mb-2">
        <IndianRupee size={12} className="text-amber-400" />
        <span className="text-zinc-300 text-xs font-semibold">Live Cost Summary</span>
      </div>
      <div className="grid grid-cols-4 gap-2 text-center">
        <SummaryCell label="Ingredients" value={`₹${ingCost.toFixed(2)}`} />
        <SummaryCell label="Overheads" value={`₹${ohCost.toFixed(2)}`} />
        <SummaryCell label={`Total / ${bs}kg`} value={`₹${totalCost.toFixed(2)}`} accent="amber" />
        <SummaryCell label="Cost / kg" value={`₹${costPerKg.toFixed(2)}`} accent="amber" />
      </div>
      {pricing > 0 && (
        <div className={`mt-2 pt-2 border-t border-zinc-800 flex items-center justify-between text-[11px] ${
          marginBad ? 'text-red-300' : marginLow ? 'text-amber-300' : 'text-green-300'
        }`}>
          <span className="flex items-center gap-1.5">
            {marginBad && <AlertTriangle size={11} />}
            Margin @ pricing ₹{pricing.toFixed(2)}/kg
          </span>
          <span className="font-mono font-semibold">
            {marginPct?.toFixed(1)}% {marginBad ? '· LOSS' : marginLow ? '· LOW' : ''}
          </span>
        </div>
      )}
      {totalCost > 0 && onUsePricingSuggestion && (
        <button
          type="button"
          onClick={() => onUsePricingSuggestion(costPerKg)}
          className="mt-2 w-full text-[10px] text-amber-400 hover:text-amber-300 underline"
          data-testid="use-suggested-costing"
        >
          Copy ₹{costPerKg.toFixed(2)}/kg into "Costing" field
        </button>
      )}
    </div>
  );
}

function SummaryCell({ label, value, accent }) {
  return (
    <div className={`rounded-lg px-2 py-1.5 ${accent === 'amber' ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-zinc-900 border border-zinc-800'}`}>
      <div className="text-[9px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`text-xs font-mono font-bold ${accent === 'amber' ? 'text-amber-300' : 'text-zinc-100'}`}>{value}</div>
    </div>
  );
}

// ─── Trial-row inline cost / margin badge ──────────────────────────────────
function TrialCostBadge({ trial }) {
  const ingCost = (trial.ingredients || []).reduce((s, i) => s + ((parseFloat(i.qty) || 0) * (parseFloat(i.rate) || 0)), 0);
  const ohCost = (trial.overhead_costs || []).reduce((s, o) => s + (parseFloat(o.amount) || 0), 0);
  const total = ingCost + ohCost;
  if (total <= 0) return <span className="text-zinc-600 text-[11px]">—</span>;
  const bs = parseFloat(trial.batch_size_kg) || 1;
  const cpkg = total / bs;
  const pricing = parseFloat(trial.pricing) || 0;
  const marginPct = pricing > 0 ? ((pricing - cpkg) / pricing) * 100 : null;
  const bad = marginPct !== null && marginPct < 0;
  const low = marginPct !== null && marginPct >= 0 && marginPct < 15;
  return (
    <div className="inline-flex flex-col items-start gap-0.5">
      <span className="font-mono text-amber-300 text-xs font-semibold">₹{cpkg.toFixed(0)}/kg</span>
      {marginPct !== null && (
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${
          bad ? 'bg-red-900/30 text-red-300 border-red-700'
          : low ? 'bg-amber-900/30 text-amber-300 border-amber-700'
          : 'bg-green-900/30 text-green-300 border-green-700'
        }`} title={`Margin vs pricing ₹${pricing}/kg`}>
          {marginPct.toFixed(0)}% {bad ? 'LOSS' : low ? 'LOW' : ''}
        </span>
      )}
    </div>
  );
}

// ─── R&D AI Chat (ChatGPT-style, admin-only, multi-turn) ────────────────────
function RndChat() {
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Hi! I'm your R&D assistant. Ask me to formulate a new blend, tweak a recipe, find ingredient substitutes, or troubleshoot a trial. I have full visibility into recipes, stock, and recent trials." },
  ]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(`rnd-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setInput('');
    setBusy(true);
    try {
      const res = await axios.post(`${API}/rnd/chat`,
        { message: text, session_id: sessionRef.current },
        { withCredentials: true });
      sessionRef.current = res.data.session_id || sessionRef.current;
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.reply || '—' }]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'R&D AI unavailable');
      setMessages(prev => [...prev, { role: 'assistant', content: '_Error contacting R&D AI._' }]);
    } finally { setBusy(false); }
  };

  const suggestions = [
    'Suggest a low-sodium peri-peri seasoning under ₹180/kg',
    'What can substitute red chilli powder if we run low?',
    'Formulate a biryani masala using only what I have in stock',
    'Why did the last Tandoori trial fail? Suggest fixes.',
  ];

  return (
    <div className="space-y-3" data-testid="rnd-chat">
      <div>
        <h1 className="font-heading text-2xl font-semibold text-zinc-50 flex items-center gap-2"><Sparkles size={18} className="text-amber-400" /> R&D AI Chat</h1>
        <p className="text-zinc-500 text-xs mt-0.5">Recipe-aware assistant · session stays in context as you chat</p>
      </div>

      <div ref={scrollRef} className="bg-zinc-900 border border-zinc-800 rounded-2xl h-[55vh] overflow-y-auto p-4 space-y-3" data-testid="rnd-chat-log">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[82%] rounded-2xl px-4 py-2.5 text-sm ${
              m.role === 'user'
                ? 'bg-amber-500 text-black'
                : 'bg-zinc-800 text-zinc-100 border border-zinc-700'
            }`}>
              {m.role === 'assistant'
                ? <div className="prose prose-invert prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-li:my-0"><ReactMarkdown>{m.content}</ReactMarkdown></div>
                : m.content}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start">
            <div className="bg-zinc-800 text-zinc-400 border border-zinc-700 rounded-2xl px-4 py-2.5 text-sm animate-pulse">Thinking…</div>
          </div>
        )}
      </div>

      {messages.length <= 1 && (
        <div className="flex flex-wrap gap-2">
          {suggestions.map(s => (
            <button key={s} onClick={() => setInput(s)} data-testid={`rnd-chat-suggestion-${s.slice(0, 15).replace(/\s+/g, '-').toLowerCase()}`}
              className="text-xs px-3 py-1.5 rounded-full border border-zinc-800 bg-zinc-900 text-zinc-400 hover:text-amber-300 hover:border-amber-500/40 transition-all">
              {s}
            </button>
          ))}
        </div>
      )}

      <form onSubmit={send} className="flex gap-2">
        <input data-testid="rnd-chat-input" value={input} onChange={e => setInput(e.target.value)}
          placeholder="Ask about recipes, substitutes, costing, trials…"
          className="flex-1 bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
        <button data-testid="rnd-chat-send" type="submit" disabled={busy || !input.trim()}
          className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 disabled:bg-zinc-800 disabled:text-zinc-500 text-black font-semibold text-xs px-4 rounded-xl transition-colors">
          <Send size={14} /> Send
        </button>
      </form>
    </div>
  );
}
